import type { BondRenderField } from "@/lib/bondFields";
import { formatBondValue } from "@/lib/bondValueDisplay";
import { sentenceCaseKey } from "@/lib/listFormat";

// BRK-S140 — how a filed bond order's terms READ as a record.
//
// The final step captures twenty-odd values in one flat, configuration-driven
// bag. Rendered in arrival order that is a form somebody has already filled in;
// what an underwriter and a broker reading the same order need is the four
// questions it answers: who the bond is FOR, what the WORK is, what the BOND's
// own terms are, and anything else the configuration asked for.
//
// ── WHY A RULE AND NOT A LIST ────────────────────────────────────────────────
//
// The field set is discovery's, and it grows without a portal release — that is
// the whole point of a configuration-driven step. So a hand-maintained
// `["obligeeName", "obligeeType", …]` would silently DROP the first field Greg
// adds, or worse, drop it into no section at all. Instead the classification is
// a small set of key rules plus a GENERIC FALLBACK, and the fallback is the part
// that matters: an unclassified field is still rendered, in its own section,
// rather than disappearing because nobody taught this module about it.
//
// Deliberately keyed on the FIELD KEY and not on the label, which is display and
// changes (PMS-S317 relabelled `bondAmount` to "Bond penalty" and kept the key
// precisely because the key is contract). And deliberately not on the TYPE: two
// currency fields can belong to different sections, and one section holds three
// types.

/** The sections, in the order a broker reads them. */
export type BondProfileSectionId = "obligee" | "project" | "terms" | "other";

export interface BondProfileSection {
  id: BondProfileSectionId;
  title: string;
  fields: BondRenderField[];
}

const TITLES: Record<BondProfileSectionId, string> = {
  obligee: "Obligee",
  project: "Project",
  terms: "Bond terms",
  // Named for what it IS rather than "Other": a broker reading a section called
  // "Other" cannot tell whether it is a catch-all or a category Signal has.
  other: "Additional details",
};

/**
 * Fields whose section the KEY does not give away, stated outright.
 *
 * Short on purpose — every entry here is a field the rules below would put
 * somewhere defensible but wrong, and a long table would be the hand-maintained
 * list this module exists to avoid.
 */
const EXPLICIT: Record<string, BondProfileSectionId> = {
  // The price of the WORK, not a term of the bond: it is what the broker bid,
  // which is also why the bid-result panel measures against it (BRK-S141).
  contractPrice: "project",
  estimatedContractPrice: "project",
  estContractPrice: "project",
  bidAmount: "project",
  // A bid DATE is about the job going out to tender, not about the bond's term.
  bidDate: "project",
  startDate: "project",
  completionDate: "project",
};

/**
 * The key rules, IN ORDER — the first match wins, so a rule for a narrower
 * subject is listed above a broader one. Matched case-insensitively.
 *
 * Most are PREFIX rules, because a field's key leads with its subject. `bond` is
 * the one CONTAINMENT rule, and it earns the exception: `estimatedBondAmount`
 * leads with "estimated" and is unmistakably a bond term, and so would
 * `revisedBondAmount` or `finalBondPenalty`. Containment is safe HERE precisely
 * because it is ordered below `project` — `projectBondNotes` is decided by the
 * project rule first, which is why the ordering is part of the rule and not a
 * tidying detail.
 */
type KeyRule = readonly [
  pattern: string,
  section: BondProfileSectionId,
  mode?: "contains",
];

const RULES: readonly KeyRule[] = [
  ["obligee", "obligee"],
  ["project", "project"],
  ["liquidateddamages", "terms"],
  ["maintenance", "terms"],
  ["contract", "terms"],
  ["bond", "terms", "contains"],
  ["work", "terms"],
];

/**
 * Which section one field belongs to.
 *
 * Falls through to `other` rather than guessing: an unclassified field is a
 * field this build has not been taught about, and rendering it in a named
 * section it does not belong to is worse than rendering it honestly in a
 * catch-all.
 */
export function sectionForField(key: string): BondProfileSectionId {
  const explicit = EXPLICIT[key];
  if (explicit) return explicit;
  const lower = key.toLowerCase();
  for (const [pattern, section, mode] of RULES) {
    const hit =
      mode === "contains" ? lower.includes(pattern) : lower.startsWith(pattern);
    if (hit) return section;
  }
  return "other";
}

/**
 * The final step's fields, grouped into sections — CONFIGURATION ORDER within
 * each, so the record reads like the form the broker filled in.
 *
 * TWO KINDS OF EMPTY ARE DROPPED, and that is the one thing this decides beyond
 * the grouping (which is why the caller gets a list rather than a record):
 *
 *   • a section the configuration declared no fields for;
 *   • when `values` is given, a section whose fields are ALL UNANSWERED. A bond
 *     configuration asks twenty-odd mostly-optional questions, so a filed order
 *     routinely leaves a whole section blank — and a heading over "nothing
 *     captured" reads as data that failed to load rather than as a question
 *     nobody needed to answer.
 *
 * "Answered" is decided by `formatBondValue` returning a string, which is the
 * SAME rule the record itself omits a row by (`CapturedValues`). Deliberately
 * the same call rather than a local re-statement: two rules for "is this
 * answered" is how a section gets dropped while one of its rows would have
 * rendered, or kept while none of them do.
 */
export function bondProfileSections(
  fields: readonly BondRenderField[],
  /** The captured bag. Omit to group WITHOUT dropping unanswered sections. */
  values?: Readonly<Record<string, unknown>>,
): BondProfileSection[] {
  const order: BondProfileSectionId[] = [
    "obligee",
    "project",
    "terms",
    "other",
  ];
  const grouped = new Map<BondProfileSectionId, BondRenderField[]>(
    order.map((id) => [id, []]),
  );
  for (const field of fields) {
    grouped.get(sectionForField(field.name))?.push(field);
  }
  const answered = (field: BondRenderField) =>
    values === undefined ||
    formatBondValue(values[field.name], {
      type: field.type,
      options: field.options,
    }) !== null;

  return order
    .map((id) => ({ id, title: TITLES[id], fields: grouped.get(id) ?? [] }))
    .filter((section) => section.fields.some(answered));
}

/**
 * The one field whose value is the PROJECT's address, or null when the
 * configuration declares none.
 *
 * Named by its section and its TYPE rather than by a key literal: the map link
 * belongs to whichever address field is about the project, and a configuration
 * that spells it `projectSite` still gets one. The obligee's address does not —
 * a broker has no reason to drive to the county offices.
 *
 * The FIRST match, because a configuration declaring two project addresses has
 * no way to say which is the site, and two map links would be two guesses.
 */
export function projectAddressField(
  fields: readonly BondRenderField[],
): BondRenderField | null {
  return (
    fields.find(
      (field) =>
        field.type === "address" && sectionForField(field.name) === "project",
    ) ?? null
  );
}

/**
 * A section's first answered TEXT field — the project's and the obligee's
 * names for the header's "insured · project · obligee" line (BRK-S399).
 *
 * By section and TYPE, never a key literal, on `projectAddressField`'s rule:
 * a configuration that spells it `jobName` still leads the line. Null when
 * nothing in the section is answered text, and the line then says less.
 */
export function sectionLeadText(
  fields: readonly BondRenderField[],
  values: Record<string, unknown>,
  section: BondProfileSectionId,
): string | null {
  for (const field of fields) {
    if (field.type !== "text" || sectionForField(field.name) !== section) {
      continue;
    }
    const value = values[field.name];
    if (typeof value === "string" && value.trim() !== "") return value.trim();
  }
  return null;
}

/**
 * A field key as a heading — the last-resort label for a group whose field the
 * configuration no longer declares.
 *
 * Delegates to the shared `sentenceCaseKey`: field keys are camelCase and
 * document type keys are snake_case, and both want the same sentence style, so
 * a second regex here would be the near-copy that eventually differs by a
 * hyphen.
 */
export function humanizeFieldKey(key: string): string {
  return sentenceCaseKey(key);
}
